/**
 * Reads a chosen text file into a textarea, so the file's content is submitted with the form as text.
 * Expects a `[data-file-input]` file input and a `textarea` inside. Optional attributes:
 * - `data-max-bytes`: largest file accepted, in bytes.
 * - `data-too-large`: message shown in `[data-file-error]` when the file is larger than that.
 */
class FileToTextarea extends HTMLElement {
    /** Read in progress, if any. Only the latest selection may fill the textarea. */
    #reader = null;

    constructor() {
        super();

        const input = this.querySelector('[data-file-input]');
        if (input) {
            input.addEventListener('change', this);
        }
    }

    /**
     * Load the selected file into the textarea.
     */
    handleEvent() {
        const input = this.querySelector('[data-file-input]');
        const textarea = this.querySelector('textarea');
        const file = input?.files?.[0];

        // a read for an earlier selection must not finish after this one and overwrite it
        this.#reader?.abort();
        this.#reader = null;
        if (!file || !textarea) {
            return;
        }

        this.#setError('');
        const maxBytes = Number.parseInt(this.dataset.maxBytes ?? '', 10);
        if (maxBytes > 0 && file.size > maxBytes) {
            input.value = '';
            this.#setError(this.dataset.tooLarge || 'File is too large.');
            return;
        }

        const reader = new FileReader();
        this.#reader = reader;
        reader.addEventListener('load', () => {
            if (this.#reader !== reader) {
                return;
            }
            this.#reader = null;
            textarea.value = typeof reader.result === 'string' ? reader.result : '';
        });
        reader.readAsText(file);
    }

    /**
     * Show or clear the error message.
     * @param {string} message - Message to show. Empty to hide.
     */
    #setError(message) {
        const error = this.querySelector('[data-file-error]');
        if (error) {
            error.textContent = message;
            error.hidden = !message;
        }
    }
}

// Define the new web component
if ('customElements' in window) {
    customElements.define('nilla-file-to-textarea', FileToTextarea);
}

export default FileToTextarea;
